import { Box, Button, CircularProgress, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import ReactSignatureCanvas from "react-signature-canvas";
import { useCreateSignedContract } from "../../../hooks/data/data_contract";
import { IDocument } from "../../../interfaces/IDocument";
import { useSnackbar } from "../../context/SnackbarContext";
import { CustomCircularProgress } from "../../core/CustomCircularProgress";
import { PDFViewer } from "../../core/PDFViewer";
import { base64Download } from "../../core/generic";
import { ContractDocumentAnswer } from "./ContractDocumentAnswers/ContractDocumentAnswer";
import { IContractDocumentAnswer } from "../../../interfaces/IContractDocumentAnswer";
import { useGetTemplateById } from "../../../hooks/data/data_templates";
import { useGeneratePdf } from "../../../utlis/Document";
import { set } from "date-fns";

export interface ISignedContract {
    Contract: number;
    Document_Signed: IDocument;
    Document_Signed_Partner: IDocument | null;
    ContractDocumentAnswerArray: IContractDocumentAnswer[];
}

const contractTypeToTemplateId = (contractType: number | undefined): number => {
    switch (contractType) {
        case 10:
            return 4;
        case 20:
            return 5;
        case 30:
            return 6;
        case 40:
            return 7;
        default:
            return -1; // Default or error case
    }
};

interface IProps {
    idContract: number;
    enableAuftraggeberSigned?: boolean;
    contractType?: number;
    handleClose: () => void;
    handleSave: () => void;
}

export const SignedContract: React.FC<IProps> = ({
    idContract,

    enableAuftraggeberSigned = true,
    handleClose,
    handleSave,
    contractType,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const refPartner = useRef<ReactSignatureCanvas | null>(null);
    const refCustomer = useRef<ReactSignatureCanvas | null>(null);
    const { showFeedback } = useSnackbar();

    const { generatePdf, isPendingGenerateContractDocument } = useGeneratePdf();

    const [generatedData, setGeneratedData] = useState<IDocument | null>(null);

    const { template, isLoadingTemplate, refetchTemplate } = useGetTemplateById(contractTypeToTemplateId(contractType));

    const [answerArray, setAnswerArray] = useState<IContractDocumentAnswer[] | null>(null);

    const { createSignedContract, isPendingCreateSignedContract } = useCreateSignedContract();

    /* -------------- 1) Handler nur noch Zustand ändern -------------- */
    const answerArrayChangeHandler = (newAnswers: IContractDocumentAnswer[]) => {
        setAnswerArray(newAnswers); // <– reicht
    };

    /* -------------- 2) Effect: auf answerArray „horchen“ ------------- */
    const prevAnswersRef = useRef<IContractDocumentAnswer[] | null>(null);

    const refetchPdf = React.useCallback(() => {
        setIsLoading(true);
        generatePdf(
            (data: IDocument) => {
                setGeneratedData(data);
                setIsLoading(false);
            },
            () => {
                showFeedback("Die Dokumente konnten nicht generiert werden!", "error");
                setIsLoading(false);
            },
            { id: idContract },
            answerArray ?? [], // Falls answerArray null ist, leeres Array verwenden
        );
    }, [generatePdf, idContract, answerArray, showFeedback]);

    useEffect(() => {
        if (
            answerArray === null || // ❗ noch gar keine Antworten
            isPendingGenerateContractDocument ||
            template === undefined // Template noch nicht da
        ) {
            return;
        }

        if (prevAnswersRef.current && JSON.stringify(prevAnswersRef.current) === JSON.stringify(answerArray)) {
            return; // keine Änderung
        }

        prevAnswersRef.current = answerArray;
        refetchPdf(); // erzeugt das PDF
    }, [answerArray, template, isPendingGenerateContractDocument, refetchPdf]);

    const handleUpload = async () => {
        let base64StringPartner = refPartner.current?.toDataURL();
        let base64StringCustomer = refCustomer.current?.toDataURL();

        if (base64StringCustomer !== undefined) {
            let uploadObject: ISignedContract = {
                Contract: idContract,
                ContractDocumentAnswerArray: answerArray ?? [], // Falls answerArray null ist, leeres Array verwenden
                //HandoverProtocolPartner: currentApplicationDocument.HandoverProtocolPartner,
                //HandoverApplicationDocumentAnswerArray: answerArray,
                Document_Signed_Partner: null,
                Document_Signed: {
                    file_name: "signed_customer.png",
                    data: base64StringCustomer.split(",")[1],
                    id: -1,
                    MIMEtype: { id: 2, MIMEtype: "image/png", file_ending: "png" },
                },
            };
            try {
                let data = await createSignedContract(uploadObject);
                showFeedback("Der Vertrag wurde erfolgreich gespeicher!", "success");
                base64Download(data);
                handleSave();
            } catch (error) {
                console.error(error);
                showFeedback("Es ist ein Fehler aufgeretten!", "error");
            }
        }
    };
    if (isLoadingTemplate || template === undefined) {
        return <CustomCircularProgress />;
    }

    return (
        <>
            <Typography variant="h4">
                Vertrag
                <Button
                    variant="contained"
                    disabled={isPendingCreateSignedContract}
                    onClick={handleUpload}
                    sx={{ ml: 2, float: "right" }}
                >
                    {isPendingCreateSignedContract ? (
                        <Box display="flex" alignItems="center" gap={1}>
                            <span>Speichern</span>
                            <CircularProgress size={20} />
                        </Box>
                    ) : (
                        "Speichern"
                    )}
                </Button>
            </Typography>

            <Box sx={{ mt: 6 }} />
            {isLoading ? <CustomCircularProgress /> : <PDFViewer documentObject={generatedData} />}

            {contractType !== undefined && (
                <>
                    <Box sx={{ mt: 6 }} />
                    <ContractDocumentAnswer
                        id={idContract}
                        template={template}
                        answerArray={answerArray ?? []}
                        setAnswerArray={setAnswerArray}
                    />
                </>
            )}

            <Box mt={6} sx={{ float: "right", display: "flex", flexDirection: "column" }}>
                <>
                    {enableAuftraggeberSigned && <Typography>Unterschrift Autraggeber</Typography>}
                    {!enableAuftraggeberSigned && <Typography>Unterschrift Handwerkspartner</Typography>}
                    <ReactSignatureCanvas
                        penColor="black"
                        backgroundColor="rgba(238,238,238,1)"
                        canvasProps={{ width: 800, height: 300, className: "sigCanvas" }}
                        ref={refCustomer}
                    />
                </>
                <Box sx={{ mt: 6 }}>
                    <Button
                        variant="contained"
                        disabled={isPendingCreateSignedContract}
                        onClick={handleUpload}
                        sx={{ ml: 2, float: "right" }}
                    >
                        {isPendingCreateSignedContract ? (
                            <Box display="flex" alignItems="center" gap={1}>
                                <span>Speichern</span>
                                <CircularProgress size={20} />
                            </Box>
                        ) : (
                            "Speichern"
                        )}
                    </Button>
                </Box>
            </Box>
        </>
    );
};
